import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import dayjs from "dayjs";
import logoSvg from "../img/systems-logo.svg";

const loadLogoAsDataUrl = () =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => resolve(null);
    img.src = logoSvg;
  });

const generateTraceabilityPDF = async ({ operations, filters, userName }) => {
  const logoDataUrl = await loadLogoAsDataUrl();
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "letter" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 40;

  // Group operations by board_id
  const boardMap = new Map();
  operations.forEach((op) => {
    const key = op.board_id || op.id;
    if (!boardMap.has(key)) boardMap.set(key, []);
    boardMap.get(key).push(op);
  });

  const totalOps = operations.length;
  const totalBoards = boardMap.size;
  const successCount = operations.filter((o) => o.success).length;
  const successRate = totalOps > 0 ? ((successCount / totalOps) * 100).toFixed(1) : "0.0";

  // Board type breakdown
  const byType = {};
  boardMap.forEach((ops) => {
    const type = ops[0].board_type || "Unknown";
    byType[type] = (byType[type] || 0) + 1;
  });

  // Station breakdown
  const byStation = {};
  operations.forEach((op) => {
    const station = op.station_id || "Unknown";
    byStation[station] = (byStation[station] || 0) + 1;
  });

  const dateRange =
    filters.fromDate && filters.toDate
      ? `${dayjs(filters.fromDate).format("MMM D, YYYY")} – ${dayjs(filters.toDate).format("MMM D, YYYY")}`
      : filters.fromDate
        ? `From ${dayjs(filters.fromDate).format("MMM D, YYYY")}`
        : filters.toDate
          ? `Through ${dayjs(filters.toDate).format("MMM D, YYYY")}`
          : "All Time";

  // ─── Title page ───
  let y = 80;
  if (logoDataUrl) {
    const logoW = 200;
    const logoH = 200 * (352 / 1377); // preserve aspect ratio
    doc.addImage(logoDataUrl, "PNG", (pageW - logoW) / 2, y, logoW, logoH);
    y += logoH + 30;
  }

  doc.setFontSize(24);
  doc.setFont("helvetica", "bold");
  doc.text("Manufacturing Traceability Report", pageW / 2, y, { align: "center" });
  y += 40;

  doc.setFontSize(12);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(100);

  const meta = [
    `Date Range: ${dateRange}`,
    filters.boardType ? `Board Type: ${filters.boardType}` : null,
    filters.stationId ? `Station: ${filters.stationId}` : null,
    `Generated: ${dayjs().format("MMM D, YYYY h:mm A")}`,
    userName ? `Generated by: ${userName}` : null,
  ].filter(Boolean);

  meta.forEach((line) => {
    doc.text(line, pageW / 2, y, { align: "center" });
    y += 20;
  });

  doc.setTextColor(0);

  // ─── Summary page ───
  doc.addPage();
  y = margin;

  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text("Summary", margin, y);
  y += 25;

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [["Metric", "Value"]],
    body: [
      ["Total Boards Programmed", String(totalBoards)],
      ["Total Flash Operations", String(totalOps)],
      ["Successful Operations", String(successCount)],
      ["Failed Operations", String(totalOps - successCount)],
      ["Success Rate", `${successRate}%`],
      ["Date Range", dateRange],
    ],
    styles: { fontSize: 10, cellPadding: 6 },
    headStyles: { fillColor: [50, 50, 50], textColor: 255 },
    alternateRowStyles: { fillColor: [245, 245, 245] },
    columnStyles: { 0: { fontStyle: "bold", cellWidth: 200 } },
  });

  y = doc.lastAutoTable.finalY + 30;

  // Board type breakdown
  if (Object.keys(byType).length > 0) {
    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    doc.text("Boards by Type", margin, y);
    y += 15;

    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      head: [["Board Type", "Count"]],
      body: Object.entries(byType).sort((a, b) => b[1] - a[1]),
      styles: { fontSize: 10, cellPadding: 5 },
      headStyles: { fillColor: [50, 50, 50], textColor: 255 },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      columnStyles: { 0: { fontStyle: "bold" } },
    });

    y = doc.lastAutoTable.finalY + 30;
  }

  // Station breakdown
  if (Object.keys(byStation).length > 0) {
    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    doc.text("Operations by Station", margin, y);
    y += 15;

    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      head: [["Station", "Operations"]],
      body: Object.entries(byStation).sort((a, b) => b[1] - a[1]),
      styles: { fontSize: 10, cellPadding: 5 },
      headStyles: { fillColor: [50, 50, 50], textColor: 255 },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      columnStyles: { 0: { fontStyle: "bold" } },
    });
  }

  // ─── Board detail pages ───
  doc.addPage();
  y = margin;

  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text("Board Detail", margin, y);
  y += 15;

  // Build rows: first op per board shows board info, subsequent show blanks in board columns
  const detailRows = [];
  boardMap.forEach((ops, boardId) => {
    const sorted = [...ops].sort(
      (a, b) => new Date(a.operation_timestamp) - new Date(b.operation_timestamp)
    );
    sorted.forEach((op, idx) => {
      const isFirst = idx === 0;
      detailRows.push({
        boardType: isFirst ? (op.board_type || "-") : "",
        primaryUid: isFirst ? (op.primary_uid?.slice(0, 16) || "-") : "",
        serial: isFirst ? (op.drone_serialnumber || "-") : "",
        timestamp: dayjs(op.operation_timestamp).format("MM/DD/YY HH:mm"),
        station: op.station_id || "-",
        operator: op.operator_firstname
          ? `${op.operator_firstname} ${(op.operator_lastname || "").charAt(0)}.`
          : "-",
        firmware: op.firmware_file || "-",
        result: op.success ? "PASS" : "FAIL",
        duration: op.duration_seconds ? `${op.duration_seconds}s` : "-",
        error: op.error_message || "",
        isFirst,
        success: op.success,
      });
    });
  });

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [
      [
        "Type",
        "Primary UID",
        "Serial",
        "Timestamp",
        "Station",
        "Operator",
        "Firmware",
        "Result",
        "Duration",
        "Error",
      ],
    ],
    body: detailRows.map((r) => [
      r.boardType,
      r.primaryUid,
      r.serial,
      r.timestamp,
      r.station,
      r.operator,
      r.firmware,
      r.result,
      r.duration,
      r.error,
    ]),
    styles: { fontSize: 7, cellPadding: 3, overflow: "ellipsize" },
    headStyles: { fillColor: [50, 50, 50], textColor: 255, fontSize: 7 },
    columnStyles: {
      0: { cellWidth: 35 },
      1: { cellWidth: 85, font: "courier" },
      2: { cellWidth: 60, font: "courier" },
      7: { cellWidth: 35, halign: "center" },
      8: { cellWidth: 40, halign: "center" },
      9: { cellWidth: 80 },
    },
    didParseCell: (data) => {
      if (data.section === "body") {
        const row = detailRows[data.row.index];
        // Color-code result column
        if (data.column.index === 7) {
          data.cell.styles.textColor = row.success ? [22, 163, 74] : [220, 38, 38];
          data.cell.styles.fontStyle = "bold";
        }
        // Gray out blank board columns on continuation rows
        if (!row.isFirst && data.column.index <= 2) {
          data.cell.styles.textColor = [200, 200, 200];
        }
        // Add top border for board group separators
        if (row.isFirst && data.row.index > 0) {
          data.cell.styles.lineWidth = { top: 0.5 };
          data.cell.styles.lineColor = { top: [180, 180, 180] };
        }
      }
    },
  });

  // ─── Page numbers ───
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(150);
    doc.text(
      `Firefly Systems - Manufacturing Traceability Report`,
      margin,
      pageH - 20
    );
    doc.text(`Page ${i} of ${totalPages}`, pageW - margin, pageH - 20, {
      align: "right",
    });
  }

  doc.save(`Traceability_Report_${dayjs().format("YYYY-MM-DD")}.pdf`);
};

export default generateTraceabilityPDF;
